//#region Imports
import { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import '../../../../scss/Components/_modal.scss';
import customAxios from '../../../../utils/customAxios';
import { GlobalVariables } from '../../../../globalVar';
import { getToast } from '../../../../utils/toastConfig';

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

const UpdateClientTiModal = ({ show, onHide, getData, clientTiId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [number, setNumber] = useState('');
  const [position, setPosition] = useState('');
  const [loadingData, setLoadingData] = useState(false);

  const resetFormFields = () => {
    setName('');
    setLastName('');
    setEmail('');
    setNumber('');
    setPosition('');
  };

  // Cargar datos del ClientTi cuando se abre el modal
  const loadClientTiData = async () => {
    if (!clientTiId) return;

    setLoadingData(true);
    try {
      const endpoint = `${GlobalVariables.getURL()}client-ti/getClientTiById/${clientTiId}`;
      const response = await customAxios.get(endpoint, HEADERS);

      if (response.data.ok && response.data.data) {
        setName(response.data.data.name || '');
        setLastName(response.data.data.last_name || '');
        setEmail(response.data.data.email || '');
        setNumber(response.data.data.number ?? '');
        setPosition(response.data.data.position || '');
      }
    } catch (error) {
      console.log('Error loading ClientTi data:', error);
      getToast({
        status: 'error',
        title: 'Error al cargar los datos del Contacto de Sucursal.',
      });
    } finally {
      setLoadingData(false);
    }
  };

  const updateClientTi = async (event) => {
    event.preventDefault();

    if (!name || name.trim().length === 0) {
      getToast({
        status: 'error',
        title: 'El nombre del Contacto de Sucursal no puede estar vacío.',
      });
      return;
    }

    if (!lastName || lastName.trim().length === 0) {
      getToast({
        status: 'error',
        title: 'El apellido del Contacto de Sucursal no puede estar vacío.',
      });
      return;
    }

    if (!email || email.trim().length === 0) {
      getToast({
        status: 'error',
        title: 'El correo del Contacto de Sucursal no puede estar vacío.',
      });
      return;
    }

    setIsLoading(true);

    try {
      const endpoint = `${GlobalVariables.getURL()}client-ti/updateClientTi/${clientTiId}`;
      const response = await customAxios.put(
        endpoint,
        {
          name: name.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          number: number !== '' && number !== null ? Number(number) : null,
          position: position.trim(),
        },
        HEADERS,
      );
      console.log(response, ' response');

      if (response.status === 200) {
        hide();
        getData();
      }
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({
        status: 'error',
        title:
          error.response?.data?.message ||
          'Error al actualizar Contacto de Sucursal. Vuelve a intentarlo.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hide = () => {
    onHide();
    getToast({
      status: 'success',
      title:
        'Contacto de Sucursal actualizado exitosamente en la base de datos.',
    });
    resetFormFields();
  };

  const handleClose = () => {
    onHide();
    resetFormFields();
  };

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (show && clientTiId) {
      loadClientTiData();
    }
  }, [show, clientTiId]);

  return (
    <Dialog open={show} maxWidth="md" fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography className="inventory-movmodal-title">
          Editar Contacto de Sucursal
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          width: 40,
          height: 40,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent dividers>
        {loadingData ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '2rem',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <form id="update-clientti-form" onSubmit={updateClientTi}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre (s)"
                  placeholder="Nombre (s)..."
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Apellido (s)"
                  placeholder="Apellido (s)..."
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  placeholder="Correo Electrónico..."
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Número"
                  placeholder="Número..."
                  type="number"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Puesto"
                  placeholder="Puesto..."
                  type="text"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  required
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          form="update-clientti-form"
          type="submit"
          disabled={isLoading || loadingData}
        >
          {isLoading ? (
            <CircularProgress size="2rem" />
          ) : (
            <Typography>Actualizar</Typography>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateClientTiModal;
